import React, { useState, useContext, useEffect } from "react"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { boxShadow, MEDIA_MIN_MEDIUM } from "../../constants"
import Button from "../ui/Button"
import dayjs from "dayjs"
import { useCookies } from "react-cookie"
import { useLocation } from "@reach/router"
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies"
import { BrinkContext } from "../context/BrinkContext"
import Backdrop from "../ui/Backdrop"
import Link from "../Link"

const Container = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: ${(p) => p.theme.colors.background};
  padding: 3rem 2rem 2.4rem;
  box-shadow: ${boxShadow};
  transition: opacity 0.3s;
  z-index: ${(p) => (p.visible ? "1000000" : "-1")};
  opacity: ${(p) => (p.visible ? "1" : "0")};
  font-size: 1.3rem;
  max-width: 70rem;
  line-height: 2rem;

  p {
    margin: 0.5rem 0 2rem;
  }

  a {
    margin-left: 0.5rem;
    white-space: nowrap;
  }

  h2 {
    border-left: 0.4rem solid ${(p) => p.theme.colors.primary};
    padding-left: 1rem;
  }

  ${MEDIA_MIN_MEDIUM} {
    top: 50%;
    bottom: auto;
    right: auto;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.4rem;
    padding: 2.5rem 3rem 2rem;

    p {
      padding-right: 2rem;
      margin: 1rem 0;
    }
  }
`

const Close = styled.div`
  position: absolute;
  width: 4rem;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  cursor: pointer;
`

const SaveButton = styled(Button)`
  margin: 0 auto 1rem;
  white-space: nowrap;
  background: ${(p) => p.theme.colors.black};
  color: ${(p) => p.theme.colors.white};

  i {
    margin-right: 1rem;
    font-size: 1.6rem;
  }
`

const Section = styled.div`
  width: 100%;
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.lightBorder};
  padding: 1.5rem 0 3rem;

  &:last-of-type {
    border-bottom: none;
  }

  ${MEDIA_MIN_MEDIUM} {
    padding: 3.2rem 0 3rem;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
`

const SwitchContainer = styled.div`
  background: ${(p) =>
    p.toggled ? p.theme.colors.black : p.theme.colors.mediumGrey};
  width: 4.8rem;
  height: 2.4rem;
  position: relative;
  border-radius: 1.2rem;
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? "0.7" : "1")};
  pointer-events: ${(p) => (p.disabled ? "none" : "all")};
`

const Switch = styled.div`
  position: absolute;
  width: 2rem;
  height: 2rem;
  background: white;
  border-radius: 1rem;
  left: ${(p) => (p.toggled ? "auto" : "0.2rem")};
  right: ${(p) => (p.toggled ? "0.2rem" : "auto")};
  top: 0.2rem;
`

const ReadMoreLink = styled(Link)`
  text-align: center;
  margin-bottom: 3rem;
  margin-top: 3rem;
  display: block;
  margin-left: auto !important;
  margin-right: auto;
`

const SettingsPopup = ({ setBannerVisible }) => {
  const { t } = useTranslation("translations")
  const location = useLocation()
  const { cookieSettingsVisible, setCookieSettingsVisible } =
    useContext(BrinkContext)
  const [cookies, setCookie, removeCookie] = useCookies([
    "gdprGoogleAnalytics",
    "cookieBanner"
  ])
  const { cookieBanner, gdprGoogleAnalytics } = cookies
  const [analytics, setAnalytics] = useState(gdprGoogleAnalytics === "true")
  const [advertising, setAdvertising] = useState(
    cookieBanner === "1" || cookieBanner === "2"
  )
  const [information, setInformation] = useState(
    cookieBanner === "1" || cookieBanner === "3"
  )

  const consent =
    advertising && information
      ? "1"
      : advertising
      ? "2"
      : information
      ? "3"
      : null

  const save = () => {
    setCookie("cookieBanner", consent, {
      path: "/",
      expires: dayjs().add(180, "days").toDate()
    })

    // Google Analytics
    if (analytics) {
      initializeAndTrack(location)
    } else {
      removeCookie("_ga", {
        path: "/"
      })
      removeCookie("_gid", {
        path: "/"
      })
    }

    setCookie("gdprGoogleAnalytics", analytics, {
      path: "/",
      expires: dayjs().add(180, "days").toDate()
    })
  }

  useEffect(() => {
    setAdvertising(cookieBanner === "1" || cookieBanner === "2")
    setInformation(cookieBanner === "1" || cookieBanner === "3")
    setAnalytics(gdprGoogleAnalytics === "true")
  }, [
    cookieBanner,
    gdprGoogleAnalytics,
    setAnalytics,
    setAdvertising,
    setInformation
  ])

  return (
    <>
      <Backdrop
        handleOnClick={() => setCookieSettingsVisible(false)}
        active={cookieSettingsVisible}
      />
      <Container visible={cookieSettingsVisible}>
        <Close
          onClick={() => {
            setCookieSettingsVisible(false)
            setBannerVisible(false)
          }}
        >
          <i className="fal fa-times"></i>
        </Close>
        <h2>{t("Cookie settings")}</h2>
        <Section>
          <Header>
            {t("Technically required cookies")}
            <SwitchContainer toggled disabled>
              <Switch toggled />
            </SwitchContainer>
          </Header>
        </Section>
        <Section>
          <Header>
            {t("Cookies for statistical and analysis")}
            <SwitchContainer
              onClick={() => setAnalytics(!analytics)}
              toggled={analytics}
            >
              <Switch toggled={analytics} />
            </SwitchContainer>
          </Header>
        </Section>
        <Section>
          <Header>
            {t("Cookies for advertising")}
            <SwitchContainer
              onClick={() => setAdvertising(!advertising)}
              toggled={advertising}
            >
              <Switch toggled={advertising} />
            </SwitchContainer>
          </Header>
        </Section>
        <Section>
          <Header>
            {t("Functionality cookie information")}
            <SwitchContainer
              onClick={() => setInformation(!information)}
              toggled={information}
            >
              <Switch toggled={information} />
            </SwitchContainer>
          </Header>
        </Section>
        <ReadMoreLink
          color="black"
          underline
          to="/cookie-policy/"
          onClick={() => setCookieSettingsVisible(false)}
        >
          {t("Read more about cookies")}
        </ReadMoreLink>
        <SaveButton
          onClick={() => {
            save()
            setCookieSettingsVisible(false)
            setBannerVisible(false)
          }}
        >
          {t("Save my settings")}
        </SaveButton>
      </Container>
    </>
  )
}

export default SettingsPopup
